import React, { useState, useEffect } from 'react';
import { createBatchStamps } from '../../api/images';

const DashboardCard = ({ title, children, className }) => {
  return (
    <div className={`p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md transition-colors ${className}`}>
      <h2 className="mb-4 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">{title}</h2>
      {children}
    </div>
  );
};

const BatchStampsTool = ({ collectionId, availableFiles }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [sizeUnit, setSizeUnit] = useState('arcmin');
  const [fileSearchText, setFileSearchText] = useState('');
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [summary, setSummary] = useState(null);

  // Update filtered files whenever availableFiles or search text changes
  useEffect(() => {
    if (!availableFiles || !fileSearchText) {
      setFilteredFiles(availableFiles || []);
      return;
    }
    const lowerCaseSearch = fileSearchText.toLowerCase();
    const filtered = availableFiles.filter(file => {
      const filename = file.filename?.toLowerCase() || '';
      const filter = file.filter?.toLowerCase() || '';
      const objectName = file.object_name?.toLowerCase() || '';
      return filename.includes(lowerCaseSearch) ||
             filter.includes(lowerCaseSearch) ||
             objectName.includes(lowerCaseSearch);
    });
    setFilteredFiles(filtered);
  }, [availableFiles, fileSearchText]);

  const handleCsvChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setCsvFile(file);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSummary(null);
    setDownloadUrl(null);

    // Validation
    if (!selectedFile) {
      setError('Please select an image file');
      return;
    }

    if (!csvFile) {
      setError('Please select a CSV file');
      return;
    }

    setLoading(true);

    try {
      const result = await createBatchStamps(
        selectedFile.id,
        csvFile,
        sizeUnit
      );

      // Create a URL for the returned tar.gz blob
      const url = URL.createObjectURL(result.data);
      setDownloadUrl(url);

      // Parse summary from headers
      const summaryHeader = result.headers['x-summary'];
      if (summaryHeader) {
        try {
          const parsedSummary = JSON.parse(summaryHeader);
          setSummary(parsedSummary);
          setSuccess(`Successfully created ${parsedSummary.successful} out of ${parsedSummary.total_requested} stamps`);
        } catch (e) {
          console.error('Failed to parse summary:', e);
        }
      }

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create batch stamps');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardCard title="Batch Stamps">
      <div className="mb-4">
        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md">
          <h3 className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-2">
            CSV Format Requirements:
          </h3>
          <ul className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-tertiary space-y-1">
            <li>• Required columns: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">ra</code>, <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">dec</code>, <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">size</code></li>
            <li>• Maximum 100 rows per batch</li>
            <li>• RA: Right ascension in degrees (0-360)</li>
            <li>• Dec: Declination in degrees (-90 to 90)</li>
            <li>• Size: Cutout size in selected units</li>
          </ul>
          <div className="mt-2 text-xs">
            <details className="cursor-pointer">
              <summary className="font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                Example CSV
              </summary>
              <pre className="mt-2 p-2 bg-white dark:bg-gray-800 rounded text-xs overflow-x-auto">
{`ra,dec,size
150.5,2.3,1.0
151.2,2.5,1.5
149.8,2.1,2.0`}
              </pre>
            </details>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
              Select Image File
            </label>
            {availableFiles && availableFiles.length > 0 ? (
              <div>
                <input
                  type="text"
                  value={fileSearchText}
                  onChange={(e) => setFileSearchText(e.target.value)}
                  placeholder="Search for files..."
                  className="mt-1 block w-full p-2 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-white dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary"
                />
                <select
                  value={selectedFile ? selectedFile.id : ''}
                  onChange={(e) => {
                    const file = availableFiles.find(f => f.id === e.target.value);
                    setSelectedFile(file);
                  }}
                  className="mt-2 block w-full p-2 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-white dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary"
                  size="5"
                  required
                >
                  <option value="">Select a file</option>
                  {filteredFiles.map((file) => (
                    <option key={file.id} value={file.id}>
                      {file.filename} {file.filter ? `[${file.filter}]` : ''} {file.object_name ? `| ${file.object_name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <input
                type="text"
                value=""
                placeholder="No files available"
                disabled
                className="mt-1 block w-full p-2 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-gray-100 text-ui-light-text-secondary"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
              Upload CSV File
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleCsvChange}
              className="mt-1 block w-full p-2 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-white dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              required
            />
            {csvFile && (
              <p className="mt-1 text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">
                Selected: {csvFile.name} ({(csvFile.size / 1024).toFixed(2)} KB)
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
              Size Unit
            </label>
            <select
              value={sizeUnit}
              onChange={(e) => setSizeUnit(e.target.value)}
              className="mt-1 block w-full p-2 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-white dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary"
            >
              <option value="arcmin">arcmin</option>
              <option value="arcsec">arcsec</option>
              <option value="pixels">pixel</option>   
            </select>
            <p className="mt-1 text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">
              Unit for the 'size' column in your CSV
            </p>
          </div>

          {error && (
            <div className="p-3 mt-4 text-sm text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
              {error}
            </div>
          )}

          {success && (
            <div className="p-3 mt-4 text-sm text-green-800 dark:text-green-200 bg-green-100 dark:bg-green-900/30 rounded-md">
              {success}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !selectedFile || !csvFile}
            className="w-full mt-4 px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <svg className="w-5 h-5 inline-block mr-2 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              'Create Batch Stamps'
            )}
          </button>
        </form>

        {/* Download Section */}
        {downloadUrl && (
          <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-md">
            <h3 className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-3">
              Batch Complete!
            </h3>
            <a
              href={downloadUrl}
              download={`batch_stamps_${selectedFile?.filename || 'output'}.tar.gz`}
              className="inline-block px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
            >
              Download tar.gz Archive
            </a>
          </div>
        )}

        {/* Summary Section */}
        {summary && (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-2">
              Processing Summary:
            </h3>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                <div className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">Total</div>
                <div className="text-lg font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary">
                  {summary.total_requested}
                </div>
              </div>
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded">
                <div className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">Successful</div>
                <div className="text-lg font-semibold text-green-700 dark:text-green-300">
                  {summary.successful}
                </div>
              </div>
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded">
                <div className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">Failed</div>
                <div className="text-lg font-semibold text-red-700 dark:text-red-300">
                  {summary.failed}
                </div>
              </div>
            </div>

            {summary.results && summary.results.length > 0 && (
              <details className="cursor-pointer">
                <summary className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-2">
                  View Detailed Results
                </summary>
                <div className="max-h-60 overflow-auto bg-ui-light-card dark:bg-ui-dark-card p-3 rounded-md">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-ui-light-border dark:border-ui-dark-border">
                        <th className="text-left p-2">#</th>
                        <th className="text-left p-2">RA</th>
                        <th className="text-left p-2">Dec</th>
                        <th className="text-left p-2">Size</th>
                        <th className="text-left p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.results.map((result, idx) => (
                        <tr key={idx} className="border-b border-ui-light-border dark:border-ui-dark-border">
                          <td className="p-2">{result.index}</td>
                          <td className="p-2">{result.ra.toFixed(4)}</td>
                          <td className="p-2">{result.dec.toFixed(4)}</td>
                          <td className="p-2">{result.size}</td>
                          <td className="p-2">
                            {result.status === 'success' ? (
                              <span className="text-green-600 dark:text-green-400">✓</span>
                            ) : (
                              <span className="text-red-600 dark:text-red-400" title={result.error}>✗</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </DashboardCard>
  );
};

export default BatchStampsTool;
